// Screen 9: AI Research Assistant — RAG chat grounded in the library.

import { useChat } from '../src/lib/use-chat.js';
import { DEFAULT_MODELS } from '../src/lib/gemini.js';
import { useAuth } from '../src/lib/auth.jsx';

function formatTimeAgo(unixSec) {
  if (!unixSec) return '';
  const diff = Math.floor(Date.now() / 1000) - unixSec;
  if (diff < 60)        return 'now';
  if (diff < 3600)      return `${Math.floor(diff / 60)}m`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 86400 / 7)}w`;
}

const ASSISTANT_SYSTEM_PROMPT =
  '당신은 학술 논문 연구를 돕는 AI 어시스턴트입니다. ' +
  '간결하고 구조화된 한국어로 답하되, 영문 학술 용어와 수식은 원문 그대로 유지하세요. ' +
  '확신이 없는 부분은 솔직히 모른다고 말하고, 출처가 분명할 때만 인용을 제시하세요.';

function AssistantScreen() {
  const { t } = useT();
  const auth = useAuth();
  const [draft, setDraft] = React.useState('');
  const [conversations, setConversations] = React.useState([]);
  const [activeConvId, setActiveConvId] = React.useState(null);
  const activeConvRef = React.useRef(null);
  const threadRef = React.useRef(null);

  const chat = useChat({
    model: 'gemini-2.5-flash',
    system: ASSISTANT_SYSTEM_PROMPT,
    onComplete: async ({ user, model }) => {
      if (!auth.user) return;
      let convId = activeConvRef.current;
      // First user message in a fresh thread → spin up a conversation row.
      if (!convId) {
        try {
          const title = user.content.slice(0, 60);
          const res = await fetch('/api/conversations', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ title }),
          });
          const body = await res.json();
          if (body?.ok) {
            convId = body.data.id;
            activeConvRef.current = convId;
            setActiveConvId(convId);
            setConversations((prev) => [{ id: convId, title, paper_id: null, created_at: body.data.created_at, updated_at: body.data.updated_at }, ...prev]);
          }
        } catch { /* fall through; we still try to post messages */ }
      }
      if (!convId) return;
      // Save both messages in order. Failures are non-fatal — the UI already
      // shows the user the response.
      const post = (m) => fetch(`/api/conversations/${convId}/messages`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(m),
      }).catch(() => {});
      await post({ role: 'user', content: user.content });
      await post({ role: 'model', content: model.content, model: model.modelId });
      // Touch list ordering — bump the active conversation to the top.
      setConversations((prev) => {
        const found = prev.find((c) => c.id === convId);
        if (!found) return prev;
        return [{ ...found, updated_at: Math.floor(Date.now() / 1000) }, ...prev.filter((c) => c.id !== convId)];
      });
    },
  });

  // Load conversation list on mount / sign-in.
  React.useEffect(() => {
    if (!auth.user) { setConversations([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/conversations?limit=50', { credentials: 'same-origin' });
        const body = await res.json();
        if (!cancelled && body?.ok) setConversations(body.data?.items ?? []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [auth.user?.id]);

  // Keep the thread pinned to the latest message while streaming.
  React.useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.messages, chat.status]);

  async function openConversation(convId) {
    activeConvRef.current = convId;
    setActiveConvId(convId);
    try {
      const res = await fetch(`/api/conversations/${convId}/messages`, { credentials: 'same-origin' });
      const body = await res.json();
      if (body?.ok) {
        chat.setMessages((body.data?.items ?? []).map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          pending: false,
        })));
      }
    } catch {}
  }

  function startNewConversation() {
    activeConvRef.current = null;
    setActiveConvId(null);
    chat.reset();
  }

  function handleSend() {
    if (!draft.trim() || chat.status === 'streaming') return;
    const text = draft;
    setDraft('');
    chat.sendMessage(text);
  }

  function handleComposerKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const showDemo = chat.messages.length === 0;

  return (
    <div className="pp pp-screen" data-screen-label="09 AI assistant">
      <Sidebar active="assistant"/>

      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.workspace'), t('crumb.assistant'), t('crumb.newconv')]}/>}
          search="Search past conversations…"
          actions={true}
        />

        <div className="pp-content">
          {/* conversation history */}
          <aside className="pp-asst-history scroll">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <div className="section-title"><span className="num">{t('as.history')}</span></div>
              <button className="btn sm ghost" onClick={startNewConversation}>
                <I name="plus" size={11}/> {t('as.new')}
              </button>
            </div>
            {auth.user ? (
              conversations.length === 0 ? (
                <div style={{ fontSize: 11.5, color: 'var(--fg-3)', padding: '12px 4px', lineHeight: 1.5 }}>
                  {t('as.history') === '히스토리'
                    ? '저장된 대화가 없습니다. 메시지를 보내면 자동 저장됩니다.'
                    : 'No saved conversations yet. Send a message to start one.'}
                </div>
              ) : (
                conversations.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => openConversation(c.id)}
                    className={'pp-conv-row' + (c.id === activeConvId ? ' is-active' : '')}
                    role="button"
                  >
                    <div className="pp-conv-tick"/>
                    <div className="col grow" style={{ minWidth: 0 }}>
                      <div className="ellipsis" style={{ fontSize: 12, color: c.id === activeConvId ? 'var(--fg-0)' : 'var(--fg-1)' }}>
                        {c.title || (c.id.slice(0, 8) + '…')}
                      </div>
                      <div className="mono" style={{ fontSize: 9.5, color: 'var(--fg-3)' }}>
                        {formatTimeAgo(c.updated_at)}
                      </div>
                    </div>
                  </div>
                ))
              )
            ) : (
              <div style={{ fontSize: 11.5, color: 'var(--fg-3)', padding: '12px 4px', lineHeight: 1.5 }}>
                대화 히스토리를 저장하려면 로그인하세요.
              </div>
            )}
          </aside>

          {/* conversation */}
          <div className="pp-asst-thread scroll" ref={threadRef}>
            <div className="pp-asst-thread-inner">
              {!showDemo && chat.messages.map((m) => (
                <div key={m.id} className={'pp-asst-msg ' + (m.role === 'user' ? 'user' : 'ai')}>
                  <div className={'pp-asst-avatar ' + (m.role === 'user' ? 'user' : 'ai')}>
                    {m.role === 'user' ? <span>U</span> : <I name="sparkles" size={14}/>}
                  </div>
                  {m.role === 'user' ? (
                    <div className="pp-asst-bubble user">{m.content}</div>
                  ) : (
                    <div className="pp-asst-response">
                      <p className="pp-asst-p" style={{ whiteSpace: 'pre-wrap' }}>
                        {m.content}
                        {m.pending && <span className="pp-asst-cursor" aria-hidden>▍</span>}
                      </p>
                    </div>
                  )}
                </div>
              ))}

              {showDemo && <>
              {/* User message */}
              <div className="pp-asst-msg user">
                <div className="pp-asst-avatar user"><span>U</span></div>
                <div className="pp-asst-bubble user">
                  {t('as.user.q')}
                </div>
              </div>

              {/* Assistant response */}
              <div className="pp-asst-msg ai">
                <div className="pp-asst-avatar ai"><I name="sparkles" size={14}/></div>
                <div className="pp-asst-response">
                  <div className="row gap-2" style={{ marginBottom: 10 }}>
                    <span className="chip ghost mono"><I name="layers" size={10}/> {t('as.retrieved')}</span>
                    <span className="chip ghost mono"><I name="beaker" size={10}/> {t('as.benchrows')}</span>
                    <span className="chip ok mono" style={{ height: 20 }}><I name="shield" size={10}/> {t('as.verified.all')}</span>
                    <span className="mono" style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--fg-3)' }}>2.4s · Haiku 4.5</span>
                  </div>

                  <p className="pp-asst-p">
                    {t('as.lead')}
                  </p>

                  {/* structured answer card — table */}
                  <div className="pp-asst-answer-card">
                    <div className="pp-asst-answer-head">
                      <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                        <I name="table" size={11} style={{ color: 'var(--acc-cyan)' }}/>
                        <span style={{ fontSize: 11, color: 'var(--acc-cyan)', letterSpacing: '0.06em', textTransform: 'uppercase' }} className="mono">{t('as.struct')}</span>
                      </div>
                      <div className="row gap-2">
                        <button className="btn sm ghost"><I name="external" size={11}/> {t('as.openbench')}</button>
                        <button className="btn sm ghost"><I name="kanban" size={11}/> {t('as.pin')}</button>
                      </div>
                    </div>
                    <div className="pp-asst-table">
                      <div className="pp-asst-th">
                        <div style={{ flex: 1.5 }}>{t('as.col.method')}</div>
                        <div style={{ flex: 1 }}>{t('as.col.success')}</div>
                        <div style={{ flex: 1.3 }}>{t('as.col.proto')}</div>
                        <div style={{ flex: 0.7 }}>{t('as.col.code')}</div>
                        <div style={{ flex: 0.6 }}>{t('as.col.cite')}</div>
                      </div>
                      <div className="pp-asst-tr is-best">
                        <div style={{ flex: 1.5 }}><span className="pp-bench-crown">★</span> OpenVLA-2</div>
                        <div style={{ flex: 1 }} className="mono">0.714</div>
                        <div style={{ flex: 1.3 }} className="mono">{t('as.proto.real')}</div>
                        <div style={{ flex: 0.7 }}><span className="chip ok" style={{ height: 18 }}>yes</span></div>
                        <div style={{ flex: 0.6 }}><CiteChip n="1"/></div>
                      </div>
                      <div className="pp-asst-tr">
                        <div style={{ flex: 1.5 }}>RoboFM-13B</div>
                        <div style={{ flex: 1 }} className="mono">0.683</div>
                        <div style={{ flex: 1.3 }} className="mono">{t('as.proto.real')} <span style={{ color: 'var(--warn)' }}>*</span></div>
                        <div style={{ flex: 0.7 }}><span className="chip ok" style={{ height: 18 }}>yes</span></div>
                        <div style={{ flex: 0.6 }}><CiteChip n="2"/></div>
                      </div>
                      <div className="pp-asst-tr">
                        <div style={{ flex: 1.5 }}>OpenVLA (v1)</div>
                        <div style={{ flex: 1 }} className="mono">0.571</div>
                        <div style={{ flex: 1.3 }} className="mono">{t('as.proto.real')}</div>
                        <div style={{ flex: 0.7 }}><span className="chip ok" style={{ height: 18 }}>yes</span></div>
                        <div style={{ flex: 0.6 }}><CiteChip n="3"/></div>
                      </div>
                    </div>
                  </div>

                  {/* warning */}
                  <div className="pp-asst-warning">
                    <I name="warning" size={13} style={{ color: 'var(--warn)' }}/>
                    <div className="col">
                      <div style={{ fontSize: 12.5, color: 'var(--fg-0)', fontWeight: 500 }}>{t('as.fair.title')}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-1)', lineHeight: 1.5, marginTop: 3 }}>
                        {t('as.fair.body')}
                      </div>
                    </div>
                  </div>

                  <p className="pp-asst-p" style={{ marginTop: 12 }}>
                    {t('as.trend')}
                  </p>

                  {/* sources */}
                  <div className="pp-asst-sources">
                    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                      <div className="section-title">{t('as.sources')}</div>
                      <button className="btn sm ghost"><I name="more" size={11}/></button>
                    </div>
                    <div className="col gap-2">
                      <SourceCard n="1" paper={PAPERS[1]} excerpt='"…71.4% on ALOHA bimanual (50 trials per task, 6 subsets), +14.3 over OpenVLA, attributed to the 38-embodiment MoE head."' page="Tbl. 3, p. 8"/>
                      <SourceCard n="2" paper={PAPERS[4]} excerpt='"On ALOHA we report 68.3% averaged over 4 of the 6 canonical subsets (kitchen, towel-fold, cup, plate)."' page="Tbl. 5, p. 9"/>
                      <SourceCard n="3" paper={{ title: 'OpenVLA: An Open-Source Vision-Language-Action Model', authors: ['M. Kim','et al.'], venue: 'CoRL 2024', date: '2024.09.11' }} excerpt='"…57.1% on the 6-subset ALOHA evaluation."' page="Tbl. 2, p. 7"/>
                    </div>
                  </div>

                  {/* follow-ups */}
                  <div className="pp-asst-followups">
                    <div className="section-title" style={{ marginBottom: 8 }}>{t('as.followups')}</div>
                    <div className="col gap-2">
                      <FollowupQ q={t('as.followups.q1')}/>
                      <FollowupQ q={t('as.followups.q2')}/>
                      <FollowupQ q={t('as.followups.q3')}/>
                    </div>
                  </div>

                  <div className="pp-asst-actions">
                    <button className="btn sm"><I name="kanban" size={12}/> {t('as.addproj')}</button>
                    <button className="btn sm"><I name="book" size={12}/> {t('as.savewiki')}</button>
                    <button className="btn sm"><I name="copy" size={12}/> {t('as.copy')}</button>
                    <button className="btn sm ghost"><I name="download" size={12}/> {t('as.export')}</button>
                    <button className="btn sm ghost" style={{ marginLeft: 'auto' }}>👎</button>
                    <button className="btn sm ghost">👍</button>
                  </div>
                </div>
              </div>
              </>}
            </div>

            {/* composer */}
            <div className="pp-asst-composer">
              <div className="pp-asst-composer-meta">
                <div className="row gap-2">
                  <span className="chip ghost mono"><I name="layers" size={10}/> {t('as.lib')}</span>
                  <span className="chip ghost mono"><I name="kanban" size={10}/> {t('as.scope')}</span>
                  <span className="chip accent mono"><I name="sparkles" size={10}/> {t('as.verified.mode')}</span>
                </div>
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)', marginLeft: 'auto' }}>{t('as.sendkey')}</span>
              </div>
              <div className="pp-asst-composer-box">
                <textarea
                  className="pp-asst-textarea"
                  rows="2"
                  placeholder={t('as.textarea')}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  disabled={chat.status === 'streaming'}
                />
                <div className="row gap-2" style={{ alignItems: 'center', padding: '6px 10px', borderTop: '1px solid var(--border-1)' }}>
                  <button className="iconbtn"><I name="paperclip" size={14}/></button>
                  <button className="iconbtn"><I name="figure" size={14}/></button>
                  <button className="iconbtn"><I name="formula" size={14}/></button>
                  <div className="row gap-1" style={{ marginLeft: 8 }}>
                    <button className="pp-mode-pill is-on">{t('as.mode.grounded')}</button>
                    <button className="pp-mode-pill">{t('as.mode.explore')}</button>
                    <button className="pp-mode-pill">{t('as.mode.compare')}</button>
                  </div>
                  {chat.status === 'streaming' ? (
                    <button
                      className="btn primary"
                      style={{ marginLeft: 'auto' }}
                      onClick={chat.stop}
                    >
                      <I name="square" size={13}/> {t('as.stop') || 'Stop'}
                    </button>
                  ) : (
                    <button
                      className="btn primary"
                      style={{ marginLeft: 'auto' }}
                      onClick={handleSend}
                      disabled={!draft.trim()}
                    >
                      <I name="send" size={13}/> {t('as.send')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* retrieval panel */}
          <RailRight id="asst-retrieval" defaultWidth={300} min={260} max={460} className="pp-asst-retrieval scroll">
            <div className="section-title" style={{ marginBottom: 10 }}><span className="num">{t('as.retrieval')}</span></div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-2)', marginBottom: 12, lineHeight: 1.5 }}>
              {t('as.retrieval.sub')}
            </div>

            <div className="col gap-2">
              <RetrievalRow rank={1} paper="OpenVLA-2" chunk="§4.3 Action heads"   score={0.94}/>
              <RetrievalRow rank={2} paper="OpenVLA-2" chunk="Tbl. 3 · ALOHA"      score={0.91}/>
              <RetrievalRow rank={3} paper="RoboFM-13B" chunk="Tbl. 5 · evals"     score={0.88}/>
              <RetrievalRow rank={4} paper="OpenVLA"    chunk="Tbl. 2"             score={0.84}/>
              <RetrievalRow rank={5} paper="RoboFM-13B" chunk="§5.2 protocol"      score={0.78} muted/>
              <RetrievalRow rank={6} paper="MobileVLA"  chunk="related work"        score={0.71} muted/>
              <RetrievalRow rank={7} paper="Stop PSNR"  chunk="evaluation"          score={0.66} muted/>
              <RetrievalRow rank={8} paper="Bridge-V3"  chunk="dataset"             score={0.62} muted/>
            </div>

            <div className="section-title" style={{ marginTop: 22, marginBottom: 8 }}>{t('as.query')}</div>
            <div className="pp-asst-query-card">
              <div className="col gap-2">
                <QInterp k={t('as.q.filters')} v={t('as.q.filters.v')}/>
                <QInterp k={t('as.q.task')}    v={t('as.q.task.v')}/>
                <QInterp k={t('as.q.warnings')} v={t('as.q.warnings.v')}/>
                <QInterp k={t('as.q.scope')}   v={t('as.q.scope.v')}/>
              </div>
            </div>

            <div className="section-title" style={{ marginTop: 22, marginBottom: 8 }}>{t('as.conf')}</div>
            <div className="pp-asst-conf">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11.5, color: 'var(--fg-1)' }}>{t('as.conf.grounding')}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--ok)' }}>0.92</span>
              </div>
              <ScoreBar value={0.92} color="var(--ok)"/>
              <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 6 }}>{t('as.conf.body')}</div>
            </div>
          </RailRight>
        </div>
      </div>
    </div>
  );
}

function CiteChip({ n }) {
  return <span className="pp-cite">{n}</span>;
}

function ConvRow({ title, time, active }) {
  return (
    <div className={'pp-conv-row' + (active ? ' is-active' : '')}>
      <div className="pp-conv-tick"/>
      <div className="col grow" style={{ minWidth: 0 }}>
        <div className="ellipsis" style={{ fontSize: 12, color: active ? 'var(--fg-0)' : 'var(--fg-1)' }}>{title}</div>
        <div className="mono" style={{ fontSize: 9.5, color: 'var(--fg-3)' }}>{time}</div>
      </div>
    </div>
  );
}

function SavedRow({ title }) {
  return (
    <div className="row gap-2" style={{ padding: '6px 10px', borderRadius: 5, color: 'var(--fg-1)', fontSize: 11.5, cursor: 'pointer' }}>
      <I name="bookmark" size={11} style={{ color: 'var(--acc-violet)' }}/>
      <span className="ellipsis">{title}</span>
    </div>
  );
}

function SourceCard({ n, paper, excerpt, page }) {
  return (
    <div className="pp-source-card">
      <div className="pp-source-num">{n}</div>
      <div className="col grow" style={{ minWidth: 0 }}>
        <div className="row gap-2" style={{ alignItems: 'baseline' }}>
          <span className="chip accent" style={{ height: 18 }}>{paper.venue}</span>
          <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{page}</span>
        </div>
        <div className="serif ellipsis" style={{ fontSize: 13, marginTop: 4 }}>{paper.title}</div>
        <div className="pp-source-excerpt">{excerpt}</div>
      </div>
      <button className="iconbtn"><I name="external" size={12}/></button>
    </div>
  );
}

function FollowupQ({ q }) {
  return (
    <button className="pp-followup-q">
      <I name="chevronRight" size={11} style={{ color: 'var(--acc-indigo)' }}/>
      <span>{q}</span>
    </button>
  );
}

function RetrievalRow({ rank, paper, chunk, score, muted }) {
  return (
    <div className={'pp-retrieval-row' + (muted ? ' is-muted' : '')}>
      <span className="mono pp-retrieval-rank">{rank}</span>
      <div className="col grow gap-1" style={{ minWidth: 0 }}>
        <div className="ellipsis" style={{ fontSize: 11.5, color: 'var(--fg-0)' }}>{paper}</div>
        <div className="ellipsis mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{chunk}</div>
      </div>
      <div className="col" style={{ alignItems: 'flex-end', width: 36 }}>
        <span className="mono" style={{ fontSize: 10.5, color: muted ? 'var(--fg-3)' : 'var(--acc-indigo)' }}>{score.toFixed(2)}</span>
        <div style={{ width: 30, height: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 999, marginTop: 4, overflow: 'hidden' }}>
          <div style={{ width: `${score * 100}%`, height: '100%', background: muted ? 'var(--fg-3)' : 'var(--acc-indigo)', boxShadow: muted ? 'none' : '0 0 4px var(--acc-indigo)' }}/>
        </div>
      </div>
    </div>
  );
}

function QInterp({ k, v }) {
  return (
    <div className="row gap-2" style={{ alignItems: 'flex-start' }}>
      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', width: 64, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0, marginTop: 2 }}>{k}</span>
      <span style={{ fontSize: 11.5, color: 'var(--fg-1)' }} className="grow">{v}</span>
    </div>
  );
}

if (!document.getElementById('pp-asst-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-asst-styles';
  s.textContent = `
  .pp-asst-history { width: 240px; flex-shrink: 0; border-right: 1px solid var(--border-1); background: var(--bg-1); padding: 18px 12px; overflow: auto; }
  .pp-conv-row { display: flex; align-items: flex-start; gap: 8px; padding: 8px 8px; border-radius: 6px; cursor: pointer; }
  .pp-conv-row:hover { background: rgba(255,255,255,0.025); }
  .pp-conv-row.is-active { background: rgba(136,150,255,0.08); }
  .pp-conv-tick { width: 5px; height: 5px; border-radius: 999px; background: var(--fg-4); margin-top: 7px; flex-shrink: 0; }
  .pp-conv-row.is-active .pp-conv-tick { background: var(--acc-indigo); box-shadow: 0 0 6px var(--acc-indigo); }

  .pp-asst-thread {
    flex: 1; min-width: 0;
    display: flex; flex-direction: column;
    overflow: hidden;
  }
  .pp-asst-thread-inner {
    flex: 1; min-height: 0; overflow: auto;
    padding: 28px 32px 16px;
    max-width: 100%;
  }

  .pp-asst-msg { display: flex; gap: 12px; margin-bottom: 24px; }
  .pp-asst-msg.user { justify-content: flex-end; flex-direction: row-reverse; }
  .pp-asst-avatar { width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; }
  .pp-asst-avatar.user { background: linear-gradient(135deg, #5B68E0, #B49BFF); color: #fff; }
  .pp-asst-avatar.ai { background: linear-gradient(135deg, rgba(136,150,255,0.15), rgba(180,155,255,0.05)); color: var(--acc-violet); border: 1px solid rgba(180,155,255,0.30); }
  .pp-asst-bubble.user { max-width: 70%; background: var(--bg-3); border: 1px solid var(--border-1); border-radius: 12px 12px 4px 12px; padding: 10px 14px; font-size: 13px; color: var(--fg-0); }
  .pp-asst-response { flex: 1; max-width: 100%; min-width: 0; }
  .pp-asst-p { font-size: 13.5px; color: var(--fg-1); line-height: 1.6; margin: 0; }
  .pp-asst-cursor {
    display: inline-block;
    margin-left: 2px;
    color: var(--acc-indigo);
    animation: pp-asst-blink 1s steps(2, start) infinite;
  }
  @keyframes pp-asst-blink { to { visibility: hidden; } }

  .pp-cite {
    display: inline-flex; align-items: center; justify-content: center;
    width: 17px; height: 17px;
    background: rgba(136,150,255,0.14);
    color: var(--acc-indigo);
    border-radius: 4px;
    font-family: var(--font-mono); font-size: 10px;
    margin: 0 2px;
    vertical-align: 2px;
    cursor: pointer;
    border: 1px solid rgba(136,150,255,0.30);
  }
  .pp-cite:hover { background: rgba(136,150,255,0.30); }

  .pp-asst-answer-card {
    margin-top: 12px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    overflow: hidden;
  }
  .pp-asst-answer-head {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px;
    background: var(--bg-3);
    border-bottom: 1px solid var(--border-1);
  }
  .pp-asst-table { padding: 0; }
  .pp-asst-th, .pp-asst-tr {
    display: flex; gap: 12px;
    padding: 9px 14px;
    align-items: center;
  }
  .pp-asst-th { font-size: 10px; color: var(--fg-3); text-transform: uppercase; letter-spacing: 0.08em; }
  .pp-asst-tr { font-size: 12px; color: var(--fg-1); border-top: 1px solid var(--border-1); }
  .pp-asst-tr.is-best { background: rgba(136,150,255,0.06); }
  .pp-asst-tr.is-best .mono { color: var(--acc-indigo); font-weight: 500; }

  .pp-asst-warning {
    margin-top: 12px;
    display: flex; gap: 10px;
    padding: 12px 14px;
    background: linear-gradient(180deg, rgba(232,184,106,0.06), transparent);
    border: 1px solid rgba(232,184,106,0.30);
    border-radius: 8px;
  }

  .pp-asst-sources { margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--border-1); }

  .pp-source-card {
    display: flex; gap: 12px;
    padding: 10px 12px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 8px;
    align-items: center;
  }
  .pp-source-num {
    width: 22px; height: 22px; border-radius: 5px;
    background: rgba(136,150,255,0.14);
    color: var(--acc-indigo);
    font-family: var(--font-mono); font-size: 11px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    border: 1px solid rgba(136,150,255,0.30);
  }
  .pp-source-excerpt {
    font-size: 11.5px; color: var(--fg-2);
    margin-top: 6px;
    padding: 6px 8px;
    background: rgba(255,255,255,0.025);
    border-left: 2px solid var(--border-2);
    font-style: italic;
    line-height: 1.5;
  }

  .pp-asst-followups { margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--border-1); }
  .pp-followup-q {
    display: flex; align-items: center; gap: 8px;
    padding: 9px 12px;
    border-radius: 6px;
    border: 1px solid var(--border-1);
    background: var(--bg-2);
    color: var(--fg-1); font-size: 12px;
    text-align: left;
  }
  .pp-followup-q:hover { border-color: rgba(136,150,255,0.40); color: var(--fg-0); }

  .pp-asst-actions {
    display: flex; gap: 6px; margin-top: 16px;
    padding-top: 12px; border-top: 1px solid var(--border-1);
  }

  .pp-asst-composer {
    padding: 14px 28px 18px;
    border-top: 1px solid var(--border-1);
    background: var(--bg-1);
  }
  .pp-asst-composer-meta {
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 8px;
  }
  .pp-asst-composer-box {
    border: 1px solid rgba(136,150,255,0.30);
    background: var(--bg-2);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 0 0 3px rgba(136,150,255,0.06), 0 8px 24px rgba(0,0,0,0.3);
  }
  .pp-asst-textarea {
    width: 100%; padding: 12px 14px; resize: none;
    border: 0; outline: 0; background: transparent;
    color: var(--fg-0); font-size: 13.5px; line-height: 1.5;
    font-family: var(--font-sans);
  }
  .pp-mode-pill {
    height: 22px; padding: 0 8px;
    border-radius: 5px;
    background: transparent; color: var(--fg-2);
    font-size: 10.5px; letter-spacing: 0.04em;
    font-family: var(--font-mono);
    cursor: pointer;
  }
  .pp-mode-pill.is-on { background: rgba(136,150,255,0.14); color: var(--acc-indigo); }

  .pp-asst-retrieval { width: 300px; flex-shrink: 0; border-left: 1px solid var(--border-1); background: var(--bg-1); padding: 18px; overflow: auto; }
  .pp-retrieval-row {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 8px; border-radius: 6px;
    cursor: pointer;
    border: 1px solid var(--border-1);
    background: var(--bg-2);
  }
  .pp-retrieval-row.is-muted { background: transparent; border-color: transparent; opacity: 0.6; }
  .pp-retrieval-rank {
    width: 18px; height: 18px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(136,150,255,0.12); color: var(--acc-indigo);
    border-radius: 4px; font-size: 10px; flex-shrink: 0;
  }
  .pp-retrieval-row.is-muted .pp-retrieval-rank { background: rgba(255,255,255,0.05); color: var(--fg-3); }

  .pp-asst-query-card { padding: 10px 12px; background: var(--bg-2); border: 1px solid var(--border-1); border-radius: 8px; }
  .pp-asst-conf { background: var(--bg-2); border: 1px solid var(--border-1); border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 6px; }
  `;
  document.head.appendChild(s);
}

export { AssistantScreen };
